import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Message } from '@/types/api';
import { renderMarkdown, stripMarkdown } from '@/lib/markdown';
import { parseForwarded } from '@/lib/forwarded';
import { detectEmbeds, MessageEmbeds } from '@/components/contentPane/embeds';
import { QUICK_REACTIONS } from '@/lib/permissions';
import { useAuthStore } from '@/stores/authStore';
import { ForwardIcon, ReplyIcon, SmileIcon, MoreHorizontalIcon } from '@/components/icons';
import { emitAppEvent } from '@/lib/appEvents';
import { useDismissable } from '@/lib/useDismissable';

export function ReplyQuote(props: {
  target: NonNullable<Message['replyTo']>;
  mine: boolean;
}) {
  const { t: tr } = useTranslation();
  const t = props.target;
  // Unified treatment for both own + foreign bubbles: bubble is dark now
  // (since the own-bubble repaint), so the quote uses the same colors
  // regardless of `mine`. The mine prop is still in the signature in
  // case we want to diverge later.
  void props.mine;
  // Strip markdown delimiters for the preview so `**bold**` doesn't show
  // literal asterisks in the tiny reply quote.
  const previewSource = t.deletedAt ? tr('messages.deletedPlaceholder') : stripMarkdown(t.content);
  const preview =
    previewSource.length > 120 ? previewSource.slice(0, 120) + '…' : previewSource;
  const author = t.author.displayName || t.author.username;
  const deleted = !!t.deletedAt;

  // Compact contextual excerpt within the message; a single keyboard target.
  // Deleted originals stay plain text; other replies page back on activation.
  const content = <>
    <span className="chat-reply-label">{tr('messages.replyContext')}</span>
    <span className="chat-reply-author">{author}</span>
    <span className={'chat-reply-preview' + (deleted ? ' italic' : '')}>{preview || tr('ui.pinned.emptyContent')}</span>
  </>;
  if (deleted) {
    return (
      <div className="chat-reply">{content}</div>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        emitAppEvent('chat:scroll-to-message', { messageId: t.id });
      }}
      title={tr('messages.jumpToReply')}
      aria-label={tr('messages.jumpToReplyOf', { name: author })}
      className="chat-reply text-left"
    >
      {content}
    </button>
  );
}

/** Build the "who reacted" tooltip: up to 8 resolved names, then "+N".
 *  Falls back to a generic hint when names can't be resolved (e.g. DMs). */
function reactionTooltip(
  tr: TFunction,
  users: string[],
  reactedByMe: boolean,
  myId: string | null,
  nameFor?: (id: string) => string | undefined,
): string {
  const names = nameFor
    ? users
        .map((id) => (id === myId ? tr('common.you') : nameFor(id)))
        .filter((n): n is string => !!n)
    : [];
  if (names.length > 0) {
    const shown = names.slice(0, 8).join(', ');
    const extra = names.length > 8 ? tr('messages.andMore', { count: names.length - 8 }) : '';
    return shown + extra;
  }
  return reactedByMe
    ? tr('messages.reactionRemoveHint')
    : tr('messages.addReaction');
}

export function ReactionRow(props: {
  reactions: Message['reactions'];
  onReact: (emoji: string) => Promise<void>;
  mine: boolean;
  /** Resolve a reactor id → display name for the hover tooltip. */
  nameFor?: (userId: string) => string | undefined;
}) {
  const { t: tr } = useTranslation();
  // `users: string[]` on each reaction group enumerates everyone who
  // tapped that emoji — Discord-style. Highlight chips I'm in so I can
  // tell at a glance which were mine, and so a second tap is read as
  // "remove my reaction" without guesswork.
  //
  // BG + text colour live in index.css against `[data-reaction-pill]`
  // because the OWN-bubble in light theme paints over an accent-coloured
  // bubble with `* { color: white }` — putting that override here as an
  // inline style would lose to the parent's selector. The data-attribute
  // + class hook lets CSS pick the right surface per context.
  const myId = useAuthStore((s) => s.user?.id ?? null);
  return (
    <div className="mt-1.5 flex w-full flex-wrap gap-1">
      {props.reactions.map((r) => {
        const reactedByMe = !!myId && r.users.includes(myId);
        return (
          <button
            key={r.emoji}
            type="button"
            data-reaction-pill
            data-reacted={reactedByMe ? 'true' : 'false'}
            aria-pressed={reactedByMe}
            aria-label={tr('messages.reactionWith', { emoji: r.emoji }) + ` (${r.count})`}
            onClick={() => props.onReact(r.emoji)}
            title={reactionTooltip(tr, r.users, reactedByMe, myId, props.nameFor)}
            className={
              // 22px, not 24: inside the bubble the pill is a footnote to the
              // message, not a control competing with it.
              'reaction-pill flex h-[22px] items-center gap-1 rounded-full px-2 transition-[background-color,transform] duration-150 active:scale-95'
            }
          >
            <span className="chat-reaction-emoji" aria-hidden>{r.emoji}</span>
            <span className="reaction-pill-count text-[11px] font-medium tabular-nums leading-none">
              {r.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function Toolbar(props: {
  mine: boolean;
  /** "expanded" — true when the quick-react row is showing. */
  pickerOpen: boolean;
  onOpenPicker: () => void;
  onClosePicker: () => void;
  onReact: (emoji: string) => Promise<void>;
  onReply: () => void;
  onForward?: () => void;
  onMore?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const { t: tr } = useTranslation();
  const actionsRef = useRef<HTMLDivElement>(null);
  const [reactionsAbove, setReactionsAbove] = useState(false);
  const reactionsRef = useRef<HTMLDivElement>(null);
  const reactionTriggerRef = useRef<HTMLButtonElement>(null);
  useDismissable(props.pickerOpen, actionsRef, props.onClosePicker);
  useLayoutEffect(() => {
    if (!props.pickerOpen) return;
    const anchor = actionsRef.current;
    const picker = reactionsRef.current;
    const scroller = anchor?.closest('[data-chat-scroller]');
    if (!anchor || !picker || !scroller) return;
    const dock = anchor.closest('.chat-surface')?.querySelector<HTMLElement>('.chat-composer-dock');
    let frame = 0;
    const place = () => {
      frame = 0;
      const bounds = scroller.getBoundingClientRect();
      const bar = anchor.getBoundingClientRect();
      picker.style.setProperty('--reaction-shift', '0px');
      const rect = picker.getBoundingClientRect();
      const height = rect.height + 4;
      const visibleBottom = dock && getComputedStyle(dock).position === 'absolute'
        ? Math.min(bounds.bottom, dock.getBoundingClientRect().top) : bounds.bottom;
      setReactionsAbove(visibleBottom - bar.bottom < height && bar.top - bounds.top >= height);
      const shift = Math.max(bounds.left + 8 - rect.left, Math.min(0, bounds.right - 8 - rect.right));
      picker.style.setProperty('--reaction-shift', `${shift}px`);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(place); };
    place();
    const observer = dock ? new ResizeObserver(schedule) : null;
    if (dock) observer?.observe(dock);
    window.addEventListener('resize', schedule);
    scroller.addEventListener('scroll', schedule, { passive: true });
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      scroller.removeEventListener('scroll', schedule);
      cancelAnimationFrame(frame);
    };
  }, [props.pickerOpen]);
  useEffect(() => {
    if (!props.pickerOpen) return;
    const actions = actionsRef.current;
    const trigger = reactionTriggerRef.current;
    actions?.querySelector<HTMLButtonElement>('.chat-quick-reactions button')?.focus({ preventScroll: true });
    return () => {
      // Do not steal focus from an outside control clicked to dismiss.
      if (trigger?.isConnected && (actions?.contains(document.activeElement) || document.activeElement === document.body)) {
        trigger.focus({ preventScroll: true });
      }
    };
  }, [props.pickerOpen]);

  return (
    <div ref={actionsRef} className="chat-actions" data-open={props.pickerOpen || undefined}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) props.onClosePicker(); }}
      onKeyDown={(event) => { if (event.key === 'Escape') props.onClosePicker(); }}>
      {props.pickerOpen && <div ref={reactionsRef} data-up={reactionsAbove || undefined} className="chat-quick-reactions" role="group" aria-label={tr('messages.reaction')}>
        {QUICK_REACTIONS.map(emoji => <button key={emoji} type="button" className="chat-emoji-action"
          aria-label={tr('messages.reactionWith', { emoji })}
          onClick={async () => { props.onClosePicker(); await props.onReact(emoji); }}><span aria-hidden>{emoji}</span></button>)}
      </div>}
      <div className="chat-action-bar" role="group" aria-label={tr('messages.actions')}>
        <button ref={reactionTriggerRef} type="button" aria-label={tr('messages.reaction')} title={tr('messages.reaction')}
          aria-expanded={props.pickerOpen} onClick={props.pickerOpen ? props.onClosePicker : props.onOpenPicker}><SmileIcon /></button>
        <button type="button" aria-label={tr('messages.reply')} title={tr('messages.reply')} onClick={props.onReply}><ReplyIcon /></button>
        {props.onForward && <button type="button" aria-label={tr('messages.forward')} title={tr('messages.forward')} onClick={props.onForward}><ForwardIcon /></button>}
        {props.onMore && <button type="button" aria-label={tr('messages.moreActions')} title={tr('messages.moreActions')} onClick={props.onMore}><MoreHorizontalIcon /></button>}
      </div>
    </div>
  );
}

/**
 * Render a message body, intercepting our v1 "forwarded" synth-format
 * (`↪ Переслано · author из channel\n> quoted`) so it shows as a tidy
 * Telegram-style pill above the quoted content instead of raw markdown.
 *
 * If the message doesn't match the forward pattern, this is just a thin
 * wrapper around renderMarkdown. The branch is cheap (a single regex).
 *
 * React.memo: MessageBody renders identical output as long as content +
 * mine stay the same. Without memo, every Toolbar / context-menu re-render
 * of the parent MessageRow forces this entire markdown + embed pass to
 * re-run for every visible bubble — a measurable ~3-4 ms hit when
 * hovering rows in a busy channel.
 */
export const MessageBody = React.memo(function MessageBody(props: {
  content: string;
  mine: boolean;
  /** Channel the message lives in — threaded to the link-preview gate
   *  (EMBED_LINKS is resolved per-channel). Absent in DM/self surfaces. */
  channelId?: string;
  /** Render a forwarded message as a quiet "от {author}" header instead of the
   *  full "Переслано" card. Used in the personal Сейф, where every
   *  saved item is technically a forward and the big card would be pure noise —
   *  Telegram "Saved Messages" keeps just the original author. */
  compactForward?: boolean;
  /** One to three visible emoji with no prose, media or reply. */
  emojiOnlyCount?: number;
}) {
  const { t } = useTranslation();
  const fwd = parseForwarded(props.content);
  // Client-side embeds — Discord's "drop a youtube link and a player
  // appears" affordance, without the server-side OG-scrape pipeline. We
  // detect image/video/youtube URLs in the message body and render a
  // small preview card below the text. Cap at 3 to avoid a giant
  // embed-only message hijacking the channel.
  const embeds = useMemo(() => detectEmbeds(props.content), [props.content]);
  if (props.emojiOnlyCount && !fwd) {
    return <span className="chat-emoji-message">{props.content.trim()}</span>;
  }
  if (!fwd) {
    return (
      <div className="flex flex-col gap-2">
        <div className="msg-text">{renderMarkdown(props.content)}</div>
        {embeds.length > 0 && <MessageEmbeds items={embeds} channelId={props.channelId} />}
      </div>
    );
  }
  // Сейф: drop the "Пересланное сообщение" card down to a single muted author
  // line — keeps attribution ("показывать пользователя") without the chrome.
  if (props.compactForward) {
    return (
      <div className="flex flex-col gap-1">
        <div className="text-xs font-medium tracking-[-0.015em] text-accent">{fwd.authorName}</div>
        <div className="whitespace-pre-wrap msg-text">{renderMarkdown(fwd.quotedBody)}</div>
        {fwd.trailing && <div className="msg-text">{renderMarkdown(fwd.trailing)}</div>}
      </div>
    );
  }
  return <div className="chat-forward">
    <div className="chat-forward-label"><ForwardIcon aria-hidden className="size-3.5" />{t('messages.forwarded')}</div>
    <div className="chat-forward-content">
      <div className="chat-forward-source">{fwd.authorName}{fwd.channelLabel && <span> · {fwd.channelLabel}</span>}</div>
      <div className="msg-text">{renderMarkdown(fwd.quotedBody)}</div>
    </div>
    {fwd.trailing && <div className="msg-text mt-1">{renderMarkdown(fwd.trailing)}</div>}
  </div>;
});
